import type { Location } from '../store/settings';

// Open-Meteo (no key). NOTE: the free tier is for non-commercial use – see CLAUDE.md to-dos.

export interface DailyForecast {
  date: string;
  code: number;
  max: number;
  min: number;
}

export interface Forecast {
  current: { temperature: number; code: number; isDay: boolean };
  daily: DailyForecast[];
}

interface ForecastResponse {
  current: { temperature_2m: number; weather_code: number; is_day: number };
  daily: { time: string[]; weather_code: number[]; temperature_2m_max: number[]; temperature_2m_min: number[] };
}

export async function fetchForecast(loc: Location, unit: 'celsius' | 'fahrenheit', signal?: AbortSignal): Promise<Forecast> {
  const params = new URLSearchParams({
    latitude: String(loc.latitude),
    longitude: String(loc.longitude),
    current: 'temperature_2m,weather_code,is_day',
    daily: 'weather_code,temperature_2m_max,temperature_2m_min',
    timezone: 'auto',
    forecast_days: '7',
    temperature_unit: unit,
  });
  const res = await fetch(`https://api.open-meteo.com/v1/forecast?${params}`, { signal });
  if (!res.ok) throw new Error(`Weather unavailable (${res.status})`);
  const d = (await res.json()) as ForecastResponse;
  return {
    current: { temperature: d.current.temperature_2m, code: d.current.weather_code, isDay: d.current.is_day === 1 },
    daily: d.daily.time.map((date, i) => ({
      date,
      code: d.daily.weather_code[i] ?? 0,
      max: d.daily.temperature_2m_max[i] ?? 0,
      min: d.daily.temperature_2m_min[i] ?? 0,
    })),
  };
}

interface GeoResponse {
  results?: Array<{ name: string; latitude: number; longitude: number; country?: string; admin1?: string }>;
}

export async function searchPlaces(query: string): Promise<Location[]> {
  const params = new URLSearchParams({ name: query, count: '6', language: navigator.language.slice(0, 2) || 'en' });
  const res = await fetch(`https://geocoding-api.open-meteo.com/v1/search?${params}`);
  if (!res.ok) throw new Error('Place search failed');
  const d = (await res.json()) as GeoResponse;
  return (d.results ?? []).map((r) => ({
    name: [r.name, r.admin1, r.country].filter(Boolean).join(', '),
    latitude: r.latitude,
    longitude: r.longitude,
  }));
}

export function currentPosition(): Promise<Location> {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) return reject(new Error('Location is not available on this device'));
    navigator.geolocation.getCurrentPosition(
      (p) =>
        resolve({
          name: 'Current location',
          latitude: Math.round(p.coords.latitude * 100) / 100,
          longitude: Math.round(p.coords.longitude * 100) / 100,
        }),
      () => reject(new Error('Location permission was denied')),
      { timeout: 15_000, maximumAge: 3_600_000 },
    );
  });
}

/** WMO weather interpretation codes → short label. */
export function describeWeather(code: number): string {
  if (code === 0) return 'Clear';
  if (code <= 2) return 'Partly cloudy';
  if (code === 3) return 'Overcast';
  if (code === 45 || code === 48) return 'Fog';
  if (code >= 51 && code <= 57) return 'Drizzle';
  if ((code >= 61 && code <= 67) || (code >= 80 && code <= 82)) return 'Rain';
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return 'Snow';
  if (code >= 95) return 'Thunderstorm';
  return 'Unknown';
}
